import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Zap } from 'lucide-react';
import type { SDQReport, SDQIssue } from '../../../types';
import { DIMENSION_LABELS, severityColor, severityBg, scoreColor } from './dashboard-utils';

// recharts type compat with React 19
const _BarChart = BarChart as any;
const _Bar = Bar as any;
const _XAxis = XAxis as any;
const _YAxis = YAxis as any;
const _Tooltip = Tooltip as any;
const _ResponsiveContainer = ResponsiveContainer as any;
const _Cell = Cell as any;

interface RemediationTabProps {
  report: SDQReport;
}

export default function RemediationTab({ report }: RemediationTabProps) {
  // Collect all issues from all dimensions
  const allIssues: (SDQIssue & { dimension: string })[] = [];
  for (const [dimName, dim] of Object.entries(report.dimensions)) {
    for (const issue of dim.issues) {
      allIssues.push({ ...issue, dimension: dimName });
    }
  }

  // Sort by severity: critical > high > medium > low > info
  const sevOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
  allIssues.sort((a, b) => (sevOrder[a.severity] ?? 5) - (sevOrder[b.severity] ?? 5));

  // Quick wins: low-effort fixes (issues with suggestions)
  const quickWins = allIssues
    .filter(i => i.suggestion && i.severity !== 'info')
    .slice(0, 3);

  // Issues by dimension for bar chart
  const dimIssueCounts = Object.entries(report.dimensions)
    .filter(([, d]) => d.issues.length > 0)
    .map(([name, d]) => ({
      name: DIMENSION_LABELS[name] || name,
      issues: d.issues.length,
      score: d.score,
    }))
    .sort((a, b) => b.issues - a.issues);

  return (
    <div className="space-y-6">
      {/* Quick Wins Banner */}
      {quickWins.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-5 h-5 text-amber-400" />
            <h3 className="text-sm font-bold text-amber-400">Quick Wins — Top Fixes</h3>
          </div>
          <div className="space-y-2">
            {quickWins.map((issue, i) => (
              <div key={i} className="flex items-start gap-3 bg-cargo-surface rounded-lg p-3 border border-cargo-border">
                <span className={`mt-0.5 px-2 py-0.5 text-xs font-semibold rounded ${severityBg(issue.severity)}`}>
                  {issue.severity.toUpperCase()}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-cargo-text">{issue.message}</p>
                  {issue.suggestion && (
                    <p className="text-xs text-green-400 mt-1">Fix: {issue.suggestion}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Issues by Dimension Chart */}
      {dimIssueCounts.length > 0 && (
        <div className="bg-cargo-surface border border-cargo-border rounded-xl p-6">
          <h3 className="text-sm font-semibold text-cargo-muted mb-4">Issues by Dimension</h3>
          <div style={{ height: Math.max(200, dimIssueCounts.length * 40) }}>
            <_ResponsiveContainer width="100%" height="100%">
              <_BarChart data={dimIssueCounts} layout="vertical" margin={{ left: 100 }}>
                <_XAxis type="number" tick={{ fontSize: 12, fill: '#8899aa' }} />
                <_YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: '#8899aa' }} width={95} />
                <_Tooltip
                  contentStyle={{ backgroundColor: '#1a2332', border: '1px solid #2a3a50', borderRadius: 8, color: '#e2e8f0' }}
                  labelStyle={{ color: '#e2e8f0' }}
                />
                <_Bar dataKey="issues" radius={[0, 4, 4, 0]}>
                  {dimIssueCounts.map((entry, i) => (
                    <_Cell key={i} fill={scoreColor(entry.score)} />
                  ))}
                </_Bar>
              </_BarChart>
            </_ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Full Issue List */}
      <div className="bg-cargo-surface border border-cargo-border rounded-xl p-6">
        <h3 className="text-sm font-semibold text-cargo-muted mb-4">
          All Issues ({allIssues.length})
        </h3>
        {allIssues.length === 0 ? (
          <p className="text-sm text-cargo-muted py-4 text-center">No issues found</p>
        ) : (
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {allIssues.map((issue, i) => (
              <div
                key={i}
                className="flex items-start gap-3 p-3 rounded-lg border border-cargo-border hover:bg-cargo-hover/50 transition-colors"
              >
                {/* Severity dot */}
                <span
                  className="w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0"
                  style={{ backgroundColor: severityColor(issue.severity) }}
                />

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${severityBg(issue.severity)}`}>
                      {issue.severity.toUpperCase()}
                    </span>
                    <span className="text-xs text-cargo-muted">
                      {DIMENSION_LABELS[issue.dimension] || issue.dimension}
                    </span>
                    {issue.field && (
                      <span className="text-xs text-cargo-muted font-mono">[{issue.field}]</span>
                    )}
                  </div>
                  <p className="text-sm text-cargo-text">{issue.message}</p>
                  {issue.suggestion && (
                    <p className="text-xs text-green-400 mt-1">Fix: {issue.suggestion}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
